import { useState, useEffect, useRef, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import api, { AppDocument, axiosDefault } from 'api';
import { Doc } from 'data/constants/Doc';
import IconBtn from 'components/common/IconBtn';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import PDFViewer from 'components/common/PDFViewer/PDFViewer';
import FilePicker from 'components/common/FilePicker/FilePicker';
import styles from './DocumentPreview.module.scss';
import { renderAsync } from 'docx-preview';
import { heicTo } from 'heic-to';

type Props = {
    doc: AppDocument | Doc | null;
    downloadDoc: (doc: AppDocument | Doc) => void;
    uploadDoc: (file: File, doc: AppDocument | Doc) => void;
};

type CombinedDocType = Doc | AppDocument | null;

const getDocFileName = (doc: CombinedDocType) => {
    if (doc) {
        if ('document_name' in doc) {
            return doc.document_name;
        }
        if ('name' in doc) {
            return doc.name;
        }
    }
    return '';
};

const isHeicFile = (contentType: string, fileUrl: string, fileName: string): boolean => {
    const lowerContentType = contentType.toLowerCase();
    if (lowerContentType.includes('image/heic') || lowerContentType.includes('image/heif')) {
        return true;
    }

    const urlPath = decodeURIComponent(fileUrl.split(/[?#]/)[0]).toLowerCase();
    if (urlPath.endsWith('.heic') || urlPath.endsWith('.heif')) {
        return true;
    }

    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.heic') || lowerName.endsWith('.heif')) {
        return true;
    }

    return false;
};

const DocumentPreview = ({ doc, downloadDoc, uploadDoc }: Props) => {
    const [pathToPDF, setPathToPDF] = useState<string | null>(null);
    const [fileType, setFileType] = useState<string | null>(null);
    const [docxBuffer, setDocxBuffer] = useState<ArrayBuffer | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const docxContainerRef = useRef<HTMLDivElement | null>(null);

    const handleDisplayPDFViewer = useCallback(
        async (id: string | number) => {
            setPathToPDF(null);
            setDocxBuffer(null);
            setFileType(null);
            setLoadError(null);
            try {
                const resp = await api.documentResource.get(id);
                const url = resp.data.url;
                if (!url) {
                    setLoadError('Document URL is missing.');
                    return;
                }
                const fileResp = await axiosDefault.get<ArrayBuffer>(url, {
                    responseType: 'arraybuffer',
                    headers: { 'Content-Type': 'binary/octet-stream' },
                });
                const contentType = String(fileResp.headers?.['content-type'] || '').toLowerCase();
                const docFileName = getDocFileName(doc);

                let fileData: ArrayBuffer = fileResp.data;
                let effectiveContentType = contentType;
                if (isHeicFile(contentType, url, docFileName)) {
                    try {
                        const heicBlob = new Blob([fileResp.data]);
                        const jpegBlob = await heicTo({
                            blob: heicBlob,
                            type: 'image/jpeg',
                            quality: 0.85,
                        });
                        fileData = await jpegBlob.arrayBuffer();
                        effectiveContentType = 'image/jpeg';
                    } catch {
                        setLoadError(
                            'This HEIC image could not be converted for preview. Try downloading the file instead.'
                        );
                        return;
                    }
                }

                const lowerName = docFileName.toLowerCase();
                const isDocx =
                    effectiveContentType.includes(
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ) ||
                    effectiveContentType.includes('application/msword') ||
                    /\.docx?$/i.test(lowerName) ||
                    /\.docx?([?#].*)?$/i.test(url);
                const isImage =
                    effectiveContentType.startsWith('image/') ||
                    /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(lowerName) ||
                    /\.(png|jpe?g|gif|webp|bmp|heic|heif)([?#].*)?$/i.test(url);
                const detectedType = isDocx ? 'docx' : isImage ? 'image' : 'pdf';

                setFileType(detectedType);

                if (detectedType === 'docx') {
                    setDocxBuffer(fileData);
                    return;
                }

                const blobType =
                    effectiveContentType ||
                    (detectedType === 'pdf' ? 'application/pdf' : undefined);
                const fileBlob = new Blob([fileData], blobType ? { type: blobType } : undefined);

                const fileBase64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(fileBlob);
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = (error) => reject(error);
                });

                if (typeof fileBase64 === 'string') {
                    setPathToPDF(fileBase64);
                }
            } catch {
                setLoadError('Unable to load document. The link may be expired or missing.');
            }
        },
        [doc]
    );

    useEffect(() => {
        if (doc) {
            handleDisplayPDFViewer(doc.id);
        }
    }, [doc, handleDisplayPDFViewer]);

    useEffect(() => {
        if (fileType !== 'docx' || !docxBuffer || !docxContainerRef.current) {
            return;
        }
        docxContainerRef.current.innerHTML = '';
        void renderAsync(docxBuffer, docxContainerRef.current);
    }, [fileType, docxBuffer]);

    const displayName = (doc: CombinedDocType) => {
        return getDocFileName(doc) || 'No name available';
    };

    const isLoading = !loadError && (fileType === 'docx' ? !docxBuffer : !pathToPDF);

    return (
        <div className={styles['document-preview']}>
            <div className={styles['side-padding']}>
                <section className={styles['document-preview__header']}>
                    <h3 className={styles['document-preview__title']}>{displayName(doc)}</h3>
                    <div>
                        <IconBtn variant="primary" onClick={() => doc && downloadDoc(doc)}>
                            <DownloadIcon fill="#282828" />
                        </IconBtn>
                    </div>
                </section>
            </div>

            {pathToPDF && fileType === 'pdf' && (
                <div className={styles['pdf-viewer__wrapper']}>
                    <div className={styles['pdf-viewer']}>
                        <PDFViewer file={pathToPDF} />
                    </div>
                </div>
            )}

            {pathToPDF && fileType === 'image' && (
                <div className={styles['pdf-viewer__wrapper']}>
                    <div className={styles['pdf-viewer']}>
                        <img src={pathToPDF} alt={displayName(doc)} style={{ margin: 'auto' }} />
                    </div>
                </div>
            )}

            {fileType === 'docx' && docxBuffer && (
                <div className={styles['pdf-viewer__wrapper']}>
                    <div className={styles['pdf-viewer']}>
                        <div ref={docxContainerRef} />
                    </div>
                </div>
            )}

            {isLoading && (
                <div className={styles.loader}>
                    <CircularProgress color="primary" />
                </div>
            )}
            {loadError && (
                <div className={styles.loader}>
                    <span>{loadError}</span>
                </div>
            )}
            <div className={styles['btn-wrapper']}>
                <FilePicker
                    accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,.heic,.heif"
                    getFile={(file) => doc && uploadDoc(file, doc)}
                >
                    <button className={styles['btn-filled']} type="button" disabled={doc === null}>
                        Reupload Document
                    </button>
                </FilePicker>
            </div>
        </div>
    );
};

export default DocumentPreview;
